import { Injectable } from '@angular/core';

import { OddsAcceptanceMode } from '@bpos';
import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { IUserRootState, UserEvents, UserSettingsService, userBettingSettingsSelector } from '@frontend/sports/user/feature';
import { LoadingIndicatorService } from '@frontend/vanilla/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { OddsAcceptance } from 'packages/sports/web/app/src/betslip-base/models/odds-acceptance.enum';
import { Observable, delay, filter, map, switchMap, tap } from 'rxjs';

import { IBetslipRootState } from '../../base/store/state';
import SettingsActions from '../settings/actions';
import BetslipErrorsActions from '../validation/actions';
import { hasOddsChangedError } from '../validation/services/utils/betslip-errors-state-utils';
import { OddsPreferencePopupActions } from './actions';
import { OddsPreferencePopupService } from './odds-preference-popup.service';
import { oddsPreferencePopupStateSelector } from './selector';
import { OddsPreferenceTracking } from './tracking.model';

@Injectable()
export class OddsPreferencePopupEffects {
    constructor(
        private actions$: Actions,
        private userSettingsService: UserSettingsService,
        private userStore: Store<IUserRootState>,
        private store: Store<IBetslipRootState>,
        private redirectHelperService: RedirectHelperService,
        private dispatcher: DispatcherService,
        private loading: LoadingIndicatorService,
        private oddsPreferencePopupService: OddsPreferencePopupService,
        private betslipConfig: BetslipConfig,
    ) {}

    handleIncrementingFailedBetsCounter$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.placeBetResponseErrors),
            filter((errors) => hasOddsChangedError(errors.pickErrors)),
            map((_) => OddsPreferencePopupActions.incrementCounter()),
        );
    });

    handleFailedBetsCounterIncrements$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(OddsPreferencePopupActions.incrementCounter),
                concatLatestFrom(() => this.store.select(oddsPreferencePopupStateSelector)),
                filter(([_, state]) => this.oddsPreferencePopupService.shouldShowReminder(state.disabled, state.counter)),
                tap((_) => this.oddsPreferencePopupService.track(OddsPreferenceTracking.SpeedTooltipAndShow, [true])),
            );
        },
        { dispatch: false },
    );

    handleToggleClick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OddsPreferencePopupActions.acceptOddsChange),
            tap((_) => this.oddsPreferencePopupService.track(OddsPreferenceTracking.SpeedTooltipAndClick, [true])),
            concatLatestFrom(() => this.userStore.select(userBettingSettingsSelector)),
            filter(
                ([action, userSettingsState]) =>
                    (action.enable && userSettingsState.oddsAcceptanceMode !== OddsAcceptance.Any) ||
                    (!action.enable && userSettingsState.oddsAcceptanceMode !== OddsAcceptance.No),
            ),
            switchMap(([action, userSettings]) => {
                const settings = { ...userSettings };
                settings.oddsAcceptanceMode = action.enable ? OddsAcceptance.Any : OddsAcceptance.No;
                const loadingHandler = this.loading.start();
                const result = { hasError: false, mode: <OddsAcceptanceMode>settings.oddsAcceptanceMode };

                return new Observable<{ hasError: boolean; mode: OddsAcceptanceMode }>((subscriber) => {
                    this.userSettingsService
                        .set(settings)
                        .then(() => {
                            this.oddsPreferencePopupService.track(OddsPreferenceTracking.SpeedTooltipAndAnyOdds, [action.enable]);
                            this.dispatcher.dispatch(UserEvents.UserSettingsChanged, settings);
                        })
                        .catch((error) => {
                            this.oddsPreferencePopupService.track(OddsPreferenceTracking.SpeedTooltipAndAnyOdds, [false]);
                            result.hasError = true;
                            if (error.message === UserSettingsService.NOT_AUTHENTICATED) {
                                this.redirectHelperService.goToLogin();
                            }
                        })
                        .finally(() => {
                            subscriber.next(result);
                            subscriber.complete();
                            loadingHandler.done();
                        });
                });
            }),
            delay(this.betslipConfig.globalErrorVisibilityTimespan),
            filter((obj) => {
                if (obj.hasError) {
                    this.oddsPreferencePopupService.propagateError();
                } else {
                    this.oddsPreferencePopupService.disable();
                }

                return !obj.hasError;
            }),
            map((obj) => SettingsActions.setOddAcceptance({ acceptance: obj.mode })),
        );
    });
}
